import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  Card,
  CardContent,
  CardActions,
  createTheme,
  ThemeProvider,
  CssBaseline,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  RocketLaunch,
  TrendingUp,
  Engineering
} from '@mui/icons-material';
import TracsessDetail from './TracsessDetail';
import NexpenxDetail from './NexpenxDetail';
import AccountSupport from './AccountSupport';
import TracsessPrivacyPolicy from './TracsessPrivacyPolicy';
import TracsessTunePrivacyPolicy from './TracsessTunePrivacyPolicy';
import TracsessOrganizePrivacyPolicy from './TracsessOrganizePrivacyPolicy';
import TracsessTuneTerms from './TracsessTuneTerms';
import TracsessTuneRefund from './TracsessTuneRefund';
import TracsessUserTerms from './TracsessUserTerms';
import TracsessUserRefund from './TracsessUserRefund';
import TracsessOrganizeTerms from './TracsessOrganizeTerms';
import TracsessOrganizeRefund from './TracsessOrganizeRefund';
import emailjs from '@emailjs/browser';

// EmailJS Configuration - Replace with your actual values
const EMAILJS_SERVICE_ID = 'service_contact_form_px';
const EMAILJS_TEMPLATE_ID = 'template_bs68x3j';
const EMAILJS_PUBLIC_KEY = 'G2ROKNhazGew7Czx2'; 

// Create a custom theme with your brand color #022179
const theme = createTheme({
  palette: {
    primary: {
      main: '#022179', // Your custom deep blue
      light: '#3f51b5',
      dark: '#001a66',
    },
    secondary: {
      main: '#1976D2', // Professional light blue for accents and CTAs
      light: '#42A5F5',
      dark: '#1565C0',
    },
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1E293B',
      secondary: '#64748B',
    },
  },
  typography: {
    fontFamily: '"Prompt", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '3.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(2,33,121,0.08)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 30px rgba(2,33,121,0.15)',
          },
        },
      },
    },
  },
});

// Remove any remaining references to RECAPTCHA_SITE_KEY, handleRecaptcha, and setRecaptchaToken

const Home: React.FC = () => {
  const navigate = useNavigate();

  // Contact form state
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      // Initialize EmailJS with your public key
      emailjs.init(EMAILJS_PUBLIC_KEY);
      
      // Send email using EmailJS
      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: form.name,
          from_email: form.email,
          message: form.message,
          to_name: 'Prodxpress Team',
        }
      );
      
      if (result.status === 200) {
        setSubmitted(true);
        setForm({ name: '', email: '', message: '' });
      } else {
        setError('Failed to send message. Please try again.');
      }
    } catch (err) {
      console.error('EmailJS error:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Remove useEffect for reCAPTCHA

  return (
    <Box>
      {/* Header */}
      <AppBar position="fixed" elevation={0} sx={{ backgroundColor: 'white', color: 'text.primary' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img 
              src="/logo-square.png" 
              alt="Prodxpress Logo" 
              style={{ 
                height: '40px', 
                marginRight: '12px',
                maxWidth: '50px'
              }}
            />
            <img 
              src="/logo-title.png" 
              alt="PRODXPRESS" 
              style={{ 
                height: '24px', 
                maxWidth: '150px',
                objectFit: 'contain'
              }}
            />
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
            <Button color="primary" sx={{ mr: 1 }} onClick={() => scrollToSection('about')}>
              About
            </Button>
            <Button color="primary" sx={{ mr: 1 }} onClick={() => scrollToSection('products')}>
              Products
            </Button>
            <Button color="primary" sx={{ mr: 1 }} onClick={() => scrollToSection('contact')}>
              Contact
            </Button>
            <Button variant="contained" color="primary" onClick={() => scrollToSection('cta')}>
              Get Started
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box 
        sx={{ 
          pt: { xs: 8, md: 10 },
          pb: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #022179 0%, #1976D2 100%)',
          color: 'white',
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <img 
              src="/logo-title.png" 
              alt="PRODXPRESS" 
              style={{ 
                height: '60px', 
                maxHeight: '60px',
                maxWidth: '90%',
                objectFit: 'contain',
                marginTop: '40px',
                filter: 'brightness(0) invert(1)' 
              }}
            />
          </Box>
          <Typography 
            variant="h4" 
            sx={{ 
              color: 'white', 
              opacity: 0.9, 
              mt: { xs: 3, md: 4 },
              mb: { xs: 3, md: 4 },
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              px: { xs: 2, sm: 1 },
              lineHeight: 1.2
            }}
          >
            Accelerating Innovation Through Technology
          </Typography>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              color: 'white', 
              opacity: 0.8, 
              mb: { xs: 3, md: 4 }, 
              maxWidth: '800px', 
              mx: 'auto',
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              px: { xs: 3, sm: 2, md: 0 },
              lineHeight: 1.5
            }}
          >
            We're Prodxpress - where cutting-edge technology meets industry expertise. 
            From motorsports to manufacturing, we build solutions that drive progress and performance.
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1.5, md: 2 }, 
            justifyContent: 'center', 
            flexWrap: 'wrap',
            px: { xs: 2, sm: 0 }
          }}>
            <Button 
              variant="contained" 
              size="large" 
              sx={{ 
                backgroundColor: 'white', 
                color: 'primary.main', 
                '&:hover': { backgroundColor: '#f5f5f5' },
                fontSize: { xs: '0.875rem', md: '1rem' },
                px: { xs: 2, md: 3 }
              }} 
              onClick={() => scrollToSection('products')}
            >
              Explore Solutions
            </Button>
            <Button 
              variant="outlined" 
              size="large" 
              sx={{ 
                borderColor: 'white', 
                color: 'white', 
                '&:hover': { borderColor: '#f5f5f5', backgroundColor: 'rgba(255,255,255,0.1)' },
                fontSize: { xs: '0.875rem', md: '1rem' },
                px: { xs: 2, md: 3 }
              }} 
              onClick={() => scrollToSection('about')}
            >
              Learn More
            </Button>
          </Box>
        </Container>
      </Box>

      {/* About Section */}
      <Box id="about" sx={{ py: 8, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" gutterBottom sx={{ mb: 4, color: 'text.primary' }}>
            About Prodxpress
          </Typography>
          <Typography variant="h6" align="center" sx={{ mb: 6, color: 'text.secondary', maxWidth: '800px', mx: 'auto', lineHeight: 1.6 }}>
            At Prodxpress, we're dedicated to accelerating innovation across industries through 
            powerful technology solutions. Our mission is to streamline complex processes, enhance productivity, 
            and connect communities through intelligent software platforms.
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center', mt: 6 }}>
            <Box sx={{ textAlign: 'center', flex: '1 1 300px', maxWidth: '400px' }}>
              <RocketLaunch sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ color: 'text.primary' }}>
                Innovation First
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                We leverage cutting-edge technology to solve real-world problems and create solutions that drive industries forward
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', flex: '1 1 300px', maxWidth: '400px' }}>
              <TrendingUp sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ color: 'text.primary' }}>
                Performance Driven
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Every solution we build is designed to maximize efficiency, productivity, and deliver measurable results
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', flex: '1 1 300px', maxWidth: '400px' }}>
              <Engineering sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ color: 'text.primary' }}>
                Expert Engineering
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Our team of experienced engineers and designers craft robust, scalable solutions built to last
              </Typography>
            </Box>
          </Box>

          {/* Company Goals */}
          <Box sx={{ mt: 8, p: 4, backgroundColor: 'white', borderRadius: 4, boxShadow: '0 4px 20px rgba(2,33,121,0.08)' }}>
            <Typography variant="h4" align="center" gutterBottom sx={{ color: 'text.primary', mb: 4 }}>
              Our Mission & Goals
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
              <Box sx={{ flex: '1 1 250px', textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                  Streamline Operations
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Reduce complexity and increase efficiency across all business processes
                </Typography>
              </Box>
              <Box sx={{ flex: '1 1 250px', textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                  Connect Communities
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Build platforms that bring people together and foster collaboration
                </Typography>
              </Box>
              <Box sx={{ flex: '1 1 250px', textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                  Drive Innovation
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Continuously push boundaries to deliver next-generation solutions
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Products Section - Simplified */}
      <Box id="products" sx={{ py: 8, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" gutterBottom sx={{ mb: 2, color: 'text.primary' }}>
            Our Products
          </Typography>
          <Typography variant="h6" align="center" sx={{ mb: 6, color: 'text.secondary', maxWidth: '600px', mx: 'auto' }}>
            Innovative solutions designed to transform industries and empower users
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, justifyContent: 'center' }}>
            {/* Tracsess */}
            <Box sx={{ flex: '1 1 400px', maxWidth: '500px' }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <img 
                      src="/tracsess-logo.svg" 
                      alt="Tracsess Logo" 
                      style={{ 
                        height: '40px', 
                        width: '40px', 
                        marginRight: '16px',
                        filter: 'none'
                      }}
                    />
                    <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      Tracsess
                    </Typography>
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1357A6', mb: 2 }}>
                    Revolutionizing Motorsports
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
                    The complete motorsports ecosystem connecting organizers, tuners, and racers. 
                    Manage events, track performance, and optimize lap times all in one integrated platform.
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    "From booking track days to achieving personal bests - Tracsess powers the entire motorsports journey."
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 3, display: 'flex', gap: 1, flexDirection: 'column' }}>
                  <Button 
                    variant="outlined" 
                    fullWidth
                    href="https://tracsess.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      borderColor: '#1357A6',
                      color: '#1357A6',
                      '&:hover': { 
                        borderColor: '#0f4a8c',
                        backgroundColor: 'rgba(19, 87, 166, 0.04)'
                      }
                    }}
                  >
                    Visit tracsess.com
                  </Button>
                  <Button 
                    variant="contained" 
                    fullWidth
                    sx={{
                      backgroundColor: '#1357A6',
                      '&:hover': { backgroundColor: '#0f4a8c' }
                    }}
                    onClick={() => {
                      window.scrollTo(0, 0);
                      navigate('/products/tracsess');
                    }}
                  >
                    Learn More
                  </Button>
                </CardActions>
              </Card>
            </Box>

            {/* Nexpenx */}
            <Box sx={{ flex: '1 1 400px', maxWidth: '500px' }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <img 
                      src="/nexpenx-logo.svg" 
                      alt="Nexpenx Logo" 
                      style={{ 
                        height: '40px', 
                        width: '40px', 
                        marginRight: '16px',
                        filter: 'none'
                      }}
                    />
                    <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      Nexpenx
                    </Typography>
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1a4d1a', mb: 2 }}>
                    Smart Expense Tracking
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
                    Track expenses effortlessly with our 3-category system: Fundamentals, Fun, and Future Planning. 
                    Split bills with friends, visualize spending patterns, and achieve your financial goals.
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    "Take control of your finances - log expenses in seconds, achieve goals with clarity."
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 3 }}>
                  <Button 
                    variant="contained" 
                    fullWidth
                    sx={{
                      backgroundColor: '#1a4d1a',
                      '&:hover': { backgroundColor: '#155015' }
                    }}
                    onClick={() => {
                      window.scrollTo(0, 0);
                      navigate('/products/nexpenx');
                    }}
                  >
                    Learn More
                  </Button>
                </CardActions>
              </Card>
            </Box>
          </Box>

          {/* Future Products Teaser */}
          <Box sx={{ mt: 8, p: 4, backgroundColor: 'background.default', borderRadius: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom sx={{ color: 'text.primary' }}>
              More Solutions Coming Soon
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: '600px', mx: 'auto' }}>
              We're continuously developing new products across manufacturing, logistics, and other industries. 
              Stay tuned for innovative solutions that will transform how businesses operate.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box id="cta" sx={{ py: 8, backgroundColor: 'primary.main', color: 'white', textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" gutterBottom sx={{ color: 'white', mb: 3 }}>
            Ready to Accelerate Your Business?
          </Typography>
          <Typography variant="h6" sx={{ color: 'white', opacity: 0.9, mb: 4 }}>
            Partner with Prodxpress to streamline operations and drive innovation in your industry
          </Typography>
          <Button 
            variant="contained" 
            size="large" 
            sx={{ 
              backgroundColor: 'white', 
              color: 'primary.main', 
              '&:hover': { backgroundColor: '#f5f5f5' },
              mr: 2
            }}
          >
            Get Started Today
          </Button>
          <Button 
            variant="outlined" 
            size="large" 
            sx={{ 
              borderColor: 'white', 
              color: 'white', 
              '&:hover': { borderColor: '#f5f5f5', backgroundColor: 'rgba(255,255,255,0.1)' } 
            }}
            onClick={() => scrollToSection('contact')}
          >
            Contact Us
          </Button>
        </Container>
      </Box>

      {/* Contact Form Section */}
      <Box id="contact" sx={{ py: 8, backgroundColor: '#F8FAFC' }}>
        <Container maxWidth="sm">
          <Typography variant="h4" align="center" gutterBottom sx={{ color: 'primary.main', fontWeight: 700 }}>
            For Queries
          </Typography>
          {submitted ? (
            <Box sx={{ color: '#4ade80', mt: 2, fontWeight: 600, textAlign: 'center' }}>
              Thank you for reaching out! We'll get back to you soon.
            </Box>
          ) : (
            <Box component="form" onSubmit={handleFormSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
              <TextField
                label="Name"
                name="name"
                value={form.name}
                onChange={handleFormChange}
                required
                variant="filled"
                size="small"
                InputProps={{ style: { background: 'white' } }}
              />
              <TextField
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleFormChange}
                required
                variant="filled"
                size="small"
                InputProps={{ style: { background: 'white' } }}
              />
              <TextField
                label="Message"
                name="message"
                value={form.message}
                onChange={handleFormChange}
                required
                multiline
                minRows={3}
                variant="filled"
                size="small"
                InputProps={{ style: { background: 'white' } }}
              />
              {error && (
                <Typography color="error" sx={{ mb: 1 }}>{error}</Typography>
              )}
              <Button type="submit" variant="contained" color="primary" sx={{ mt: 1 }} disabled={loading}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Message'}
              </Button>
            </Box>
          )}
        </Container>
      </Box>

      {/* Footer */}
      <Box id="contact" sx={{ py: 6, backgroundColor: '#1E293B', color: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'space-between' }}>
            <Box sx={{ flex: '1 1 300px', maxWidth: '400px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" gutterBottom sx={{ color: 'white', fontWeight: 700, mb: 0 }}>
                  Prodxpress
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 3 }}>
                Accelerating innovation through technology solutions that streamline operations, 
                connect communities, and drive progress across industries.
              </Typography>
            </Box>
            
            <Box sx={{ flex: '1 1 200px', maxWidth: '300px' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                Products
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button 
                  sx={{ justifyContent: 'flex-start', color: '#CBD5E1', textTransform: 'none' }}
                  onClick={() => navigate('/products/tracsess')}
                >
                  Tracsess
                </Button>
                <Button 
                  sx={{ justifyContent: 'flex-start', color: '#CBD5E1', textTransform: 'none' }}
                  onClick={() => navigate('/products/nexpenx')}
                >
                  Nexpenx
                </Button>
                <Button sx={{ justifyContent: 'flex-start', color: '#CBD5E1', textTransform: 'none' }}>
                  More Coming Soon
                </Button>
              </Box>
            </Box>
            
            <Box sx={{ flex: '1 1 250px', maxWidth: '350px' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                Contact Info
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ color: 'white' }} />
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                    contact@prodxpress.com
                  </Typography>
                </Box>
                {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone sx={{ color: 'white' }} />
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                    +91 81222 96635
                  </Typography>
                </Box> */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn sx={{ color: 'white' }} />
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                    Gulecha Towers, No.158, Arcot Road, Vadapalani, Chennai, Tamil Nadu 600026.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid #475569', textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
              © 2025 Prodxpress. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <div className="App">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products/tracsess" element={<TracsessDetail />} />
            <Route path="/products/nexpenx" element={<NexpenxDetail />} />
            <Route path="/support/:product" element={<AccountSupport />} />
            <Route path="/privacy/tracsess" element={<TracsessPrivacyPolicy />} />
            <Route path="/privacy/tracsess-tune" element={<TracsessTunePrivacyPolicy />} />
            <Route path="/privacy/tracsess-organize" element={<TracsessOrganizePrivacyPolicy />} />
            {/* Terms and Conditions Routes */}
            <Route path="/terms/tracsess-tune" element={<TracsessTuneTerms />} />
            <Route path="/terms/tracsess" element={<TracsessUserTerms />} />
            <Route path="/terms/tracsess-organize" element={<TracsessOrganizeTerms />} />
            {/* Refund and Cancellation Policy Routes */}
            <Route path="/refund/tracsess-tune" element={<TracsessTuneRefund />} />
            <Route path="/refund/tracsess" element={<TracsessUserRefund />} />
            <Route path="/refund/tracsess-organize" element={<TracsessOrganizeRefund />} />
          </Routes>
        </div>
      </Router>
    </ThemeProvider>
  );
};

export default App; 